<script setup>
import { computed } from "vue";

/**
 * Связанные специализации и услуги — ссылочный блок внизу страницы каталога.
 *
 * ЗАЧЕМ. Из 20 категорий у 10 нет ни одной подуслуги и на них почти не ведут
 * внутренние ссылки: попасть туда можно было только через сайдбар. Блок даёт
 * краулеру путь к каждой категории и подуслуге с одной страницы каталога и
 * человеку — карту раздела, когда в текущей категории он ничего не нашёл.
 *
 * Данные берём из уже приходящего пропа `category` (тот же, что у CategoryFilter),
 * дополнительных запросов не делаем.
 *
 * ⚠ route() зовём только в шаблоне: в SSR-бандле глобальной route() нет,
 * ошибка в setup() уронит страницу целиком, а не деградирует блок.
 */
const props = defineProps({
  category: { type: [Array, Object], default: () => [] },
  cityUrl: { type: String, default: "" },
  cityTitle: { type: String, default: "" },
  mainUrl: { type: String, default: "" },
  secondUrl: { type: String, default: "" },
});

const cats = computed(() => Object.values(props.category || {}));

const cityPart = computed(() => (props.cityTitle ? " в городе " + props.cityTitle : ""));

/**
 * Группируем по КЛЮЧЕВОМУ слову, а не по первой букве названия.
 *
 * Почему: 13 из 46 названий начинаются с «Юрист по…», и при обычной
 * алфавитной группировке буква Ю собирала треть списка, а искать внутри
 * приходилось глазами. По ключевому слову те же 46 пунктов ложатся на 19 букв,
 * самая большая группа — 7. Служебную приставку при этом показываем бледной,
 * чтобы глаз сразу падал на то слово, по которому пункт стоит под этой буквой.
 *
 * «Консультация юриста» специально не режется: там первое слово и есть суть.
 */
const STRIP = /^(Юрист по |Юрист для |Юрист |Адвокат по |Адвокат |Юридическая |Юридический )/;

function split(name) {
  const src = String(name || "");
  const m = src.match(STRIP);
  return m ? { pre: m[0], rest: src.slice(m[0].length) } : { pre: "", rest: src };
}

/**
 * Один список на всё: и категории, и подуслуги. Разделять их смысла нет:
 * человек ищет «оспорить завещание», а не думает, специализация это или услуга.
 * Текущая страница из списка исключена.
 */
const byLetter = computed(() => {
  const flat = [];
  const add = (name, mainUrl, secondUrl, parent) => {
    const { pre, rest } = split(name);
    flat.push({ pre, rest, parent, mainUrl, secondUrl, sort: rest.toLowerCase() });
  };
  for (const c of cats.value) {
    if (!c || !c.url) continue;
    if (!(c.url === props.mainUrl && !props.secondUrl)) add(c.usl_name, c.url, "", "");
    for (const s of c.mainhassecond || []) {
      if (!s || !s.url || s.url === props.secondUrl) continue;
      add(s.usl_name, c.url, s.url, c.usl_name);
    }
  }
  const groups = new Map();
  for (const it of flat) {
    const L = (it.rest || "#").trim().charAt(0).toUpperCase();
    if (!groups.has(L)) groups.set(L, []);
    groups.get(L).push(it);
  }
  return [...groups.entries()]
    .map(([letter, items]) => ({
      letter,
      items: items.sort((a, b) => a.sort.localeCompare(b.sort, "ru")),
    }))
    .sort((a, b) => a.letter.localeCompare(b.letter, "ru"));
});

const total = computed(() => byLetter.value.reduce((n, g) => n + g.items.length, 0));
</script>

<template>
  <section
    v-if="cityUrl && total"
    id="specialties"
    class="max-w-7xl mx-auto px-4 py-8 md:py-12 scroll-mt-24"
  >
    <div class="flex items-center gap-3 mb-6">
      <span class="flex-grow h-px bg-gray-200"></span>
      <h2 class="text-2xl lg:text-3xl font-extrabold tracking-tight text-gray-900 text-center">
        Связанные специализации и услуги
      </h2>
      <span class="flex-grow h-px bg-gray-200"></span>
    </div>

    <!-- Газетные колонки: группа с буквой не разрывается между колонками -->
    <div class="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-x-8">
      <div v-for="g in byLetter" :key="g.letter" class="break-inside-avoid mb-5">
        <p class="text-sm font-bold text-indigo-600 mb-1">{{ g.letter }}</p>
        <ul class="space-y-0.5">
          <li v-for="it in g.items" :key="it.mainUrl + '/' + it.secondUrl">
            <a
              :href="it.secondUrl
                ? route('offer.second', [cityUrl, it.mainUrl, it.secondUrl])
                : route('offer.main', [cityUrl, it.mainUrl])"
              class="group block py-1.5 text-sm text-gray-800 hover:text-indigo-700 hover:underline transition-colors"
              :title="it.parent || null"
            ><span v-if="it.pre" class="text-gray-500 group-hover:text-indigo-700">{{ it.pre }}</span>{{ it.rest }}</a>
          </li>
        </ul>
      </div>
    </div>

    <a
      :href="route('uslugi.url', [cityUrl])"
      class="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
    >
      Все услуги юристов{{ cityPart }}
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </a>
  </section>
</template>
